import * as React from "react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@/components/atomics/button";
import LevelProgressBar from "@/components/atomics/level-progress-bar";
import { LevelUpMessage } from "@/components/atomics/level-up-message";
import {
  calcLevelByExperience,
  getExperienceToLevel,
} from "@/utils/experience";
import { backend } from "@/services/backend";
import QuestLevelProgressBar from "./quest-level-progress";

interface RewardSuccessModalProps {
  className?: string;
  onClose: () => void;
  rewardAmount: number;
  previousExperience: number;
  address: string;
  isBonus?: boolean;
}

export default function RewardSuccessModal({
  className,
  onClose,
  rewardAmount,
  previousExperience,
  address,
  isBonus = false,
}: RewardSuccessModalProps) {
  const navigate = useNavigate();
  const [showLevelUpMessage, setShowLevelUpMessage] = useState<boolean>(false);
  const [animationPhase, setAnimationPhase] = useState<"phase1" | "phase2">(
    "phase1"
  );
  const [rank, setRank] = useState<number | null>(null);

  useEffect(() => {
    const fetchRank = async () => {
      try {
        const fetchedRank = await backend.getWalletRatingPosition(
          address,
          "experience"
        );
        setRank(fetchedRank || null);
      } catch (error) {
        console.error("Error fetching rank:", error);
      }
    };
    if (address) {
      fetchRank();
    }
  }, [address]);

  const handleRankClick = () => {
    navigate("/leaderboard/users/level");
  };

  const exp = rewardAmount;
  const calculatedCurrentExperience = Number(previousExperience) + Number(exp);
  const previousLevel = calcLevelByExperience(previousExperience);
  const currentLevel = calcLevelByExperience(calculatedCurrentExperience);
  const hasLeveledUp = previousLevel < currentLevel;

  const levelThreshold = hasLeveledUp
    ? getExperienceToLevel(previousLevel + 1)
    : getExperienceToLevel(currentLevel + 1);
  const phase1CurrentExp = hasLeveledUp
    ? levelThreshold - 1
    : calculatedCurrentExperience;
  const phase1XpGain = hasLeveledUp ? phase1CurrentExp - previousExperience : 0;

  const phase2CurrentExp = calculatedCurrentExperience;
  const phase2XpGain = exp;

  const displayCurrentExp = !hasLeveledUp
    ? calculatedCurrentExperience
    : hasLeveledUp && animationPhase === "phase1"
      ? phase1CurrentExp
      : phase2CurrentExp;

  const displayXpGain =
    exp === 0
      ? 0
      : !hasLeveledUp
        ? exp
        : hasLeveledUp && animationPhase === "phase1"
          ? phase1XpGain
          : phase2XpGain;

  const handleReach100 = () => {
    if (hasLeveledUp && animationPhase === "phase1") {
      setShowLevelUpMessage(true);
    }
  };

  const handleLevelUpMessageClick = () => {
    setShowLevelUpMessage(false);
    setAnimationPhase("phase2");
  };

  return (
    <>
      {showLevelUpMessage && (
        <LevelUpMessage
          level={currentLevel}
          onClick={handleLevelUpMessageClick}
        />
      )}

      <div
        className={`${className} ${showLevelUpMessage ? "blur-[4px] pointer-events-none" : "opacity-100 pointer-events-auto"}`}
      >
        <div className="relative">
          <header className="relative flex justify-start items-center pl-[20px] pr-[20px] pt-[12px] pb-[12px] bg-[#FE9400]">
            <div className="text-[28px] ">
              <span>{isBonus ? "UNSTOPPABLE!" : "NICE WORK, HERO!"}</span>
            </div>
            <div
              className="absolute right-[20px] w-[21px] h-[29px] hover:cursor-pointer"
              onClick={onClose}
            >
              <img src="/assets/cancel.svg" alt="cancel" />
            </div>
          </header>
          <main className="flex flex-col justify-center items-center pt-[14px] pb-[30px] pl-[40px] pr-[40px] text-[24px]">
            <p className="flex flex-col items-center">
              <span
                className={`text-center text-[24px] uppercase ${isBonus ? "text-primary-yellow" : "text-white"}`}
              >
                {isBonus ? "YOUR BONUSES:" : "Your Rewards:"}
              </span>
              <span
                className={`text-[48px] py-[20px] font-bold ${isBonus ? "text-primary-yellow" : "text-white"}`}
              >
                {rewardAmount} EXP
              </span>
            </p>

            {exp > 0 && (
              <div className="pt-[20px] w-full">
                <QuestLevelProgressBar
                  rank={rank}
                  currentExperience={displayCurrentExp}
                  hasLeveledUp={hasLeveledUp && animationPhase === "phase2"}
                  xpGain={displayXpGain}
                  onReach100={handleReach100}
                  onClickRank={handleRankClick}
                />
              </div>
            )}

            <div className="pt-[30px] w-full">
              <Button
                className="w-full custom-aspect-320-63 text-[24px] hover:cursor-pointer hover:scale-105 transition-transform duration-300 ease-in-out"
                image="/assets/yellow-button.svg"
                text="OK"
                onClick={onClose}
              />
            </div>
          </main>
        </div>
      </div>
    </>
  );
}
